import { canPreviewWorkflowRuleGroups } from "../workflow-rule-groups.ts";
import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import { getReviewerTypeTitle } from "@/pages/workflows/edit/actions/WorkflowEditActionPerformanceReviewModal/helpers";
import {
  WorkflowEditActionPerformanceReviewEnrollFormData,
  WorkflowEditActionPerformanceReviewEnrollReviewerTypeFormData,
} from "@/pages/workflows/edit/actions/WorkflowEditActionPerformanceReviewEnrollModal/types";
import { ApiPerformanceReviewReviewerTypes } from "@/types/performance-review.types.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import { useRuleConditionTypes } from "@/queries/use-rules.query.ts";
import { Avatar, Box, Stack, Switch, Title } from "@skailer-com/uikit";
import type { Control, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";

const isReadOnlyFactReviewerType = (reviewerType: ApiPerformanceReviewReviewerTypes): boolean => {
  return (
    reviewerType === ApiPerformanceReviewReviewerTypes.myself ||
    reviewerType === ApiPerformanceReviewReviewerTypes.manager ||
    reviewerType === ApiPerformanceReviewReviewerTypes.upward
  );
};

const isParticipantsConfigurableReviewerType = (
  reviewerType: ApiPerformanceReviewReviewerTypes
): boolean => {
  return (
    reviewerType === ApiPerformanceReviewReviewerTypes.peer ||
    reviewerType === ApiPerformanceReviewReviewerTypes.custom
  );
};

interface WorkflowPerformanceReviewEnrollReviewerTypeCardProps {
  control: Control<WorkflowEditActionPerformanceReviewEnrollFormData>;
  reviewerType: WorkflowEditActionPerformanceReviewEnrollReviewerTypeFormData;
  reviewerTypeIndex: number;
  setValue: UseFormSetValue<WorkflowEditActionPerformanceReviewEnrollFormData>;
}

const WorkflowPerformanceReviewEnrollReviewerTypeCard = ({
  control,
  reviewerType,
  reviewerTypeIndex,
  setValue,
}: WorkflowPerformanceReviewEnrollReviewerTypeCardProps) => {
  const { t } = useTranslation();
  const participantConditionTypes = useRuleConditionTypes(
    RuleRegistryPlace.workflowPerformanceReviewEnrollReviewers
  );

  const isReadOnlyFactType = isReadOnlyFactReviewerType(reviewerType.reviewer_type);
  const canConfigureParticipants = isParticipantsConfigurableReviewerType(
    reviewerType.reviewer_type
  );

  return (
    <Stack className={"rounded-xs border border-dark/10 bg-dark/5 p-4"}>
      <Switch
        className={"self-start"}
        checked={true}
        disabled
        onChange={() => {}}
        title={getReviewerTypeTitle(t, reviewerType.reviewer_type, reviewerType.title)}
      />

      {!isReadOnlyFactType && canConfigureParticipants && (
        <Box className={"ml-10"}>
          <Title
            header={t("performance_review:configure_participants")}
            size={"xs"}
            variant="secondary"
            paddingTop
            paddingBottom
          />
          <RuleGroupsEditor
            control={control}
            fieldName={`reviewer_types.${reviewerTypeIndex}.participants_rule_groups` as const}
            setValue={setValue}
            place={RuleRegistryPlace.workflowPerformanceReviewEnrollReviewers}
            countEmployees={canPreviewWorkflowRuleGroups(
              reviewerType.participants_rule_groups,
              participantConditionTypes.conditionTypeInfos
            )}
            conditionTypes={participantConditionTypes.conditionTypes}
            variant="list"
            isAddRuleButtonFullWidth={false}
          />
        </Box>
      )}
    </Stack>
  );
};

interface WorkflowPerformanceReviewEnrollRulesConversionWarningProps {
  shouldShow: boolean;
  transformableParticipantsRuleTitles: string[];
}

const WorkflowPerformanceReviewEnrollRulesConversionWarning = ({
  shouldShow,
  transformableParticipantsRuleTitles,
}: WorkflowPerformanceReviewEnrollRulesConversionWarningProps) => {
  const { t } = useTranslation();

  if (!shouldShow) return null;

  return (
    <Box className={"mt-6 bg-dark/5 border-dark/5 border rounded-xs p-5"}>
      <Stack gap={"md"}>
        <Avatar emoji={":information_source:"} size={"md"} />
        <Title
          header={t("workflow:performance_review_enroll_rules_conversion_warning_title", {
            rules: transformableParticipantsRuleTitles.join(", "),
          })}
          size={"sm"}
        />
        <Title
          header={t("workflow:performance_review_enroll_rules_conversion_warning_header")}
          caption={t("workflow:performance_review_enroll_rules_conversion_warning_caption")}
          size={"sm"}
        />
      </Stack>
    </Box>
  );
};

interface WorkflowPerformanceReviewEnrollReviewerTypesSectionProps {
  control: Control<WorkflowEditActionPerformanceReviewEnrollFormData>;
  setValue: UseFormSetValue<WorkflowEditActionPerformanceReviewEnrollFormData>;
  reviewerTypes: WorkflowEditActionPerformanceReviewEnrollReviewerTypeFormData[];
  shouldShowRulesConversionWarning: boolean;
  transformableParticipantsRuleTitles: string[];
}

export const WorkflowPerformanceReviewEnrollReviewerTypesSection = ({
  control,
  setValue,
  reviewerTypes,
  shouldShowRulesConversionWarning,
  transformableParticipantsRuleTitles,
}: WorkflowPerformanceReviewEnrollReviewerTypesSectionProps) => {
  const { t } = useTranslation();

  if (!reviewerTypes.length) return null;

  return (
    <>
      <Stack>
        <WorkflowPerformanceReviewEnrollRulesConversionWarning
          shouldShow={shouldShowRulesConversionWarning}
          transformableParticipantsRuleTitles={transformableParticipantsRuleTitles}
        />
        <Stack>
          <Title
            header={t("performance_review:reviewers")}
            caption={t("performance_review:reviewers_description")}
            paddingBottom
            paddingTop
          />
          <Stack gap={"sm"}>
            {reviewerTypes.map((reviewerType, index) => (
              <WorkflowPerformanceReviewEnrollReviewerTypeCard
                key={`${reviewerType.id}-${index}`}
                control={control}
                reviewerType={reviewerType}
                reviewerTypeIndex={index}
                setValue={setValue}
              />
            ))}
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};
